import type { TerraformCommandKind } from "../../shared/terraform";
import type {
  TerraformSelectionProjection,
  TerraformStateProjection,
} from "../../shared/terraform-projection";
import { apiJson, hostedSession, pullApiPath } from "./hosted";

export interface TerraformCommandResult {
  command: string;
  comment: { id: number; url: string; createdAt: string };
}

/**
 * Which projects this change has to plan.
 *
 * Cached server-side against the diff, so this is the request the panel can
 * afford to make the moment someone opens it — or before they do.
 */
export function loadTerraformSelection(
  repository: string,
  pullNumber: number,
): Promise<TerraformSelectionProjection> {
  return apiJson<TerraformSelectionProjection>(pullApiPath(repository, pullNumber, "terraform"));
}

/**
 * What Atlantis is doing about them.
 *
 * Four live calls on the Worker's side and no cache, because it is different a
 * few seconds later. Asked for after the panel has already drawn.
 */
export function loadTerraformState(
  repository: string,
  pullNumber: number,
): Promise<TerraformStateProjection> {
  return apiJson<TerraformStateProjection>(pullApiPath(repository, pullNumber, "terraform/state"));
}

/**
 * Ask Atlantis for a plan, an apply, or an unlock.
 *
 * `headSha` is sent so the Worker can refuse a command composed against a
 * commit that has since been pushed over — a plan the reader was looking at is
 * not the plan Atlantis would run.
 */
export async function runTerraformCommand(input: {
  repository: string;
  pullNumber: number;
  headSha: string;
  kind: TerraformCommandKind;
  project?: string;
  targets?: string[];
}): Promise<TerraformCommandResult> {
  const session = await hostedSession();
  return apiJson<TerraformCommandResult>(pullApiPath(input.repository, input.pullNumber, "terraform/commands"), {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CHEW-CSRF": session.csrfToken },
    body: JSON.stringify({
      kind: input.kind,
      project: input.project,
      targets: input.targets,
      headSha: input.headSha,
    }),
  });
}
